import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { type Token } from '@app/client';
import { Jwt } from '@app/helpers/jwt';

type AuthTokensState = {
    token: Token;
    hasPassword: boolean;
    isAuthenticated: boolean;
};

interface AuthTokensStateStore extends AuthTokensState {
    setAuthTokens: (tokensState: Token, options?: { keepPrevious?: boolean }) => void;
    setIsAuthenticated: (isAuthenticated: boolean) => void;
    setHasPassword: (hasPassword: boolean) => void;
}

const initialState: AuthTokensState = {
    token: {
        access_token: '',
        refresh_token: '',
        requires_2fa: undefined,
        has_password: undefined,
    },
    isAuthenticated: false,
    hasPassword: false,
};

export const useAuthTokens = create<AuthTokensStateStore>()(
    devtools(
        persist(
            (set) => ({
                ...initialState,
                setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
                setHasPassword: (hasPassword) => set({ hasPassword }),
                setAuthTokens: (authTokens, options) =>
                    set((currState) => {
                        if (options?.keepPrevious) return { token: { ...currState.token, ...authTokens } };
                        return { token: { ...authTokens } };
                    }),
            }),
            { name: 'tokens' },
        ),
    ),
);

export function setTokens(tokens: Token, options?: { keepPrevious?: boolean }) {
    useAuthTokens.getState().setAuthTokens(tokens, options);
}

export const setIsAuthenticated = () => {
    const result = useAuthTokens.getState();
    const tokens = result.token;
    if (tokens) {
        result.isAuthenticated = false;
        result.hasPassword = !!tokens.has_password;

        try {
            const jwt = new Jwt(tokens.access_token);
            if (!jwt.isExpired()) {
                result.isAuthenticated = !tokens.requires_2fa || !jwt.is2FAExpired();
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(`Error parsing JWT: ${e}`);
        }

        if (!result.isAuthenticated) {
            result.token = { ...initialState.token, refresh_token: result.token.refresh_token };
        }
    }
    useAuthTokens.setState(result);
};

type HandleRemoveAuthTokensProps = {
    keepRefreshToken?: boolean;
};
export const handleRemoveAuthTokens = (props?: HandleRemoveAuthTokensProps) => {
    const { keepRefreshToken } = props || {};
    const state = { ...initialState };
    if (keepRefreshToken) {
        state.token.refresh_token = useAuthTokens.getState().token.refresh_token;
    }

    useAuthTokens.setState(state);
};
